'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation'; // Adicione esta linha
import { getSubscriptionSnapshot } from '@/lib/subscription';
import {
  clearDeviceFlowState,
  ensureDeviceId,
  hashDeviceTokenClient,
  DEVICE_REVOKED_COOKIE,
} from '@/lib/deviceTrust';
import { expireBrowserCookie, isLikelyAuthCookieName } from '@/lib/authCookieCleanup';
import { toast } from '@/lib/toast';
import ModalPortal from '@/components/ui/ModalPortal';
import FirstLoginVideoModal from '@/components/ui/FirstLoginVideoModal';
import OperationalProfileSwitcher from '@/components/ui/OperationalProfileSwitcher';
import { useLanguage } from '@/context/LanguageContext';
import type { GreenProOperationalProfile } from '@/lib/operationalAccounts';
import { subscribeToUserRealtime } from '@/lib/supabase/userRealtimeManager';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const isRefreshTokenError = (msg: string) =>
  msg.includes('Refresh Token') ||
  msg.includes('refresh_token') ||
  msg.includes('Invalid Refresh Token') ||
  msg.includes('Refresh Token Not Found') ||
  msg.includes('refresh_token_not_found');

const isMissingSessionError = (msg: string) => {
  const normalized = msg.toLowerCase();
  return (
    normalized.includes('auth session missing') ||
    normalized.includes('session missing') ||
    normalized.includes('session not found') ||
    normalized.includes('user from sub claim in jwt does not exist')
  );
};

const isLockError = (msg: string) => msg.includes('Lock broken') || msg.includes('AbortError');

const isRateLimitError = (msg: string, code?: string, status?: number) => {
  const normalized = msg.toLowerCase();
  return (
    status === 429 ||
    code === 'over_request_rate_limit' ||
    code === 'over_email_send_rate_limit' ||
    normalized.includes('rate limit') ||
    normalized.includes('too many requests') ||
    normalized.includes('over_request_rate_limit')
  );
};

const SESSION_CACHE_TTL_MS = 5 * 60 * 1000;
const RETRY_DELAYS_MS = [400, 900];
const SUBSCRIPTION_EXPIRING_SOON_MS = 24 * 60 * 60 * 1000;

const readCookie = (name: string) => {
  if (typeof document === 'undefined') return null;

  const prefix = `${name}=`;
  for (const rawCookie of document.cookie.split(';')) {
    const cookie = rawCookie.trim();
    if (cookie.startsWith(prefix)) {
      return decodeURIComponent(cookie.slice(prefix.length));
    }
  }

  return null;
};

const clearCookie = (name: string) => {
  if (typeof document === 'undefined') return;

  document.cookie = `${name}=; Path=/; Max-Age=0`;
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [legalAcceptanceModalOpen, setLegalAcceptanceModalOpen] = useState(false);
  const [savingLegalAcceptance, setSavingLegalAcceptance] = useState(false);
  const [introVideoModalOpen, setIntroVideoModalOpen] = useState(false);
  const [dismissingIntroVideo, setDismissingIntroVideo] = useState(false);
  const [operationalProfile, setOperationalProfileState] =
    useState<GreenProOperationalProfile>('hybrid');
  const [operationalProfileLoaded, setOperationalProfileLoaded] = useState(false);
  const [hasExplicitOperationalProfile, setHasExplicitOperationalProfile] = useState(false);
  const [operationalProfileModalOpen, setOperationalProfileModalOpen] = useState(false);
  const [savingOperationalProfile, setSavingOperationalProfile] = useState(false);
  const [subscriptionNowMs, setSubscriptionNowMs] = useState(() => Date.now());
  // Use a ref so the client is created once and never changes across re-renders
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;
  const { t } = useLanguage();
  const signingOutRef = useRef(false);
  const initializedRef = useRef(false);
  const sessionCacheRef = useRef<{ at: number; session: any } | null>(null);
  const userCacheRef = useRef<{ at: number; user: any } | null>(null);
  const sessionRequestRef = useRef<Promise<{ session: any; error: any }> | null>(null);
  const userRequestRef = useRef<Promise<{ user: any; error: any }> | null>(null);

  const clearAuthState = () => {
    setSession(null);
    setUser(null);
    setUserProfile(null);
    setOperationalProfileState('hybrid');
    setOperationalProfileLoaded(false);
    setHasExplicitOperationalProfile(false);
    setOperationalProfileModalOpen(false);
    sessionCacheRef.current = null;
    userCacheRef.current = null;
    setLoading(false);
  };

  const clearBrowserAuthArtifacts = () => {
    try {
      Object.keys(localStorage)
        .filter(
          (key) =>
            key.startsWith('sb-') ||
            key.startsWith('sb_') ||
            key.includes('supabase') ||
            key.toLowerCase().includes('auth')
        )
        .forEach((key) => localStorage.removeItem(key));
    } catch {
      // Ignore local storage cleanup failures during auth cleanup.
    }

    try {
      Object.keys(sessionStorage)
        .filter(
          (key) =>
            key.startsWith('sb-') ||
            key.startsWith('sb_') ||
            key.includes('supabase') ||
            key.toLowerCase().includes('auth')
        )
        .forEach((key) => sessionStorage.removeItem(key));
    } catch {
      // Ignore session storage cleanup failures during auth cleanup.
    }

    try {
      document.cookie.split(';').forEach((cookie) => {
        const name = cookie.trim().split('=')[0];
        if (!name) return;
        if (isLikelyAuthCookieName(name)) {
          expireBrowserCookie(name);
        }
      });
    } catch {
      // Ignore cookie cleanup failures during auth cleanup.
    }
  };

  const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const isTransientAuthError = (err: any) => {
    const msg = err?.message || '';
    const code = err?.code || '';
    const status = err?.status;
    // Nao faz retry para 429 para evitar piorar o bloqueio
    if (isRateLimitError(msg, code, status)) return false;
    return typeof status === 'number' && status >= 500;
  };

  const withAuthRetry = async <T,>(fn: () => Promise<T>, retries = 2): Promise<T> => {
    let lastError: any;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        return await fn();
      } catch (err: any) {
        lastError = err;
        if (!isTransientAuthError(err) || attempt === retries) {
          throw err;
        }
        await wait(RETRY_DELAYS_MS[attempt] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1]);
      }
    }
    throw lastError;
  };

  const getSessionWithCache = async (force = false) => {
    const now = Date.now();
    if (
      !force &&
      sessionCacheRef.current &&
      now - sessionCacheRef.current.at < SESSION_CACHE_TTL_MS
    ) {
      return { session: sessionCacheRef.current.session, error: null };
    }

    if (sessionRequestRef.current) {
      return sessionRequestRef.current;
    }

    sessionRequestRef.current = (async () => {
      try {
        const { data, error } = await withAuthRetry<any>(() => supabase.auth.getSession());
        if (!error) {
          sessionCacheRef.current = { at: Date.now(), session: data?.session ?? null };
          userCacheRef.current = { at: Date.now(), user: data?.session?.user ?? null };
        }
        return { session: data?.session ?? null, error };
      } finally {
        sessionRequestRef.current = null;
      }
    })();

    return sessionRequestRef.current;
  };

  const getUserWithCache = async (force = false) => {
    const now = Date.now();
    if (!force && userCacheRef.current && now - userCacheRef.current.at < SESSION_CACHE_TTL_MS) {
      return { user: userCacheRef.current.user, error: null };
    }

    if (userRequestRef.current) {
      return userRequestRef.current;
    }

    userRequestRef.current = (async () => {
      try {
        const { data, error } = await withAuthRetry<any>(() => supabase.auth.getUser());
        if (!error) {
          userCacheRef.current = { at: Date.now(), user: data?.user ?? null };
        }
        return { user: data?.user ?? null, error };
      } finally {
        userRequestRef.current = null;
      }
    })();

    return userRequestRef.current;
  };

  const handleStaleSession = async () => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    clearBrowserAuthArtifacts();
    clearDeviceFlowState({ preserveDeviceId: true });
    clearAuthState();
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      // Local sign-out can fail if token is already invalid.
    }
    signingOutRef.current = false;
  };

  const handleRevokedDeviceAccess = () => {
    clearCookie(DEVICE_REVOKED_COOKIE);
    clearBrowserAuthArtifacts();
    clearDeviceFlowState({ preserveDeviceId: true });
    clearAuthState();

    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.replace('/login');
      return;
    }

    if (!isAuthPublicPath) {
      router.replace('/login');
    }
  };

  const fetchOperationalProfile = useCallback(
    async (userId: string) => {
      try {
        const { data, error } = await supabase
          .from('user_preferences')
          .select('operational_profile')
          .eq('user_id', userId)
          .maybeSingle();

        if (error) throw error;

        const nextProfile =
          data?.operational_profile === 'surebet' ||
          data?.operational_profile === 'trader' ||
          data?.operational_profile === 'hybrid'
            ? (data.operational_profile as GreenProOperationalProfile)
            : 'hybrid';

        setOperationalProfileState(nextProfile);
        setHasExplicitOperationalProfile(Boolean(data?.operational_profile));
      } catch {
        setOperationalProfileState('hybrid');
        setHasExplicitOperationalProfile(false);
      } finally {
        setOperationalProfileLoaded(true);
      }
    },
    [supabase]
  );

  const fetchUserProfile = useCallback(
    async (authUser: any) => {
      const userId = authUser?.id;
      if (!userId) {
        setUserProfile(null);
        setOperationalProfileLoaded(true);
        return;
      }

      void fetchOperationalProfile(userId);

      try {
        const metadata = authUser?.user_metadata || {};
        const legalAcceptancePatch = {
          service_terms_accepted_at: metadata?.service_terms_accepted_at || null,
          service_terms_accepted_ip: metadata?.service_terms_accepted_ip || null,
          service_terms_accepted_name: metadata?.service_terms_accepted_name || null,
          service_terms_accepted_email: metadata?.service_terms_accepted_email || null,
          privacy_policy_accepted_at: metadata?.privacy_policy_accepted_at || null,
          privacy_policy_accepted_ip: metadata?.privacy_policy_accepted_ip || null,
          privacy_policy_accepted_name: metadata?.privacy_policy_accepted_name || null,
          privacy_policy_accepted_email: metadata?.privacy_policy_accepted_email || null,
        };

        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          const missingServiceTerms =
            !data.service_terms_accepted_at && legalAcceptancePatch.service_terms_accepted_at;
          const missingPrivacyPolicy =
            !data.privacy_policy_accepted_at && legalAcceptancePatch.privacy_policy_accepted_at;

          if (missingServiceTerms || missingPrivacyPolicy) {
            const { data: repairedExistingProfile } = await supabase
              .from('user_profiles')
              .update({
                ...(missingServiceTerms
                  ? {
                      service_terms_accepted_at: legalAcceptancePatch.service_terms_accepted_at,
                      service_terms_accepted_ip: legalAcceptancePatch.service_terms_accepted_ip,
                      service_terms_accepted_name: legalAcceptancePatch.service_terms_accepted_name,
                      service_terms_accepted_email:
                        legalAcceptancePatch.service_terms_accepted_email,
                    }
                  : {}),
                ...(missingPrivacyPolicy
                  ? {
                      privacy_policy_accepted_at: legalAcceptancePatch.privacy_policy_accepted_at,
                      privacy_policy_accepted_ip: legalAcceptancePatch.privacy_policy_accepted_ip,
                      privacy_policy_accepted_name:
                        legalAcceptancePatch.privacy_policy_accepted_name,
                      privacy_policy_accepted_email:
                        legalAcceptancePatch.privacy_policy_accepted_email,
                    }
                  : {}),
              })
              .eq('id', userId)
              .select('*')
              .maybeSingle();

            setUserProfile(repairedExistingProfile || data);
            return;
          }

          setUserProfile(data);
          return;
        }

        // Self-heal for environments where the auth trigger was not applied.
        const fullName =
          authUser?.user_metadata?.full_name || authUser?.email?.split('@')?.[0] || '';
        const avatarUrl = authUser?.user_metadata?.avatar_url || '';

        await supabase.from('user_profiles').upsert(
          {
            id: userId,
            email: authUser?.email || '',
            full_name: fullName,
            avatar_url: avatarUrl,
            ...legalAcceptancePatch,
          },
          { onConflict: 'id' }
        );

        const { data: repairedProfile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        setUserProfile(repairedProfile || null);
      } catch {
        setUserProfile(null);
      }
    },
    [fetchOperationalProfile, supabase]
  );

  const router = useRouter(); // Inicializa o roteador
  const pathname = usePathname(); // 2. Pegue a rota atual
  const subscriptionStatus = getSubscriptionSnapshot(userProfile, subscriptionNowMs);
  const isAuthPublicPath =
    pathname === '/login' ||
    pathname === '/forgot-password' ||
    pathname === '/update-password' ||
    pathname.startsWith('/auth/');
  const shouldDeferLegalAcceptanceModal =
    pathname === '/device-approval' ||
    pathname.startsWith('/device-approval/') ||
    pathname === '/confirm-email';
  const userNeedsLegalAcceptance = Boolean(
    user &&
    userProfile &&
    (!userProfile.service_terms_accepted_at || !userProfile.privacy_policy_accepted_at)
  );
  const userNeedsIntroVideoPrompt = Boolean(
    user && userProfile && !userProfile.platform_intro_video_seen_at
  );
  const userNeedsOperationalProfileSelection = Boolean(
    user && userProfile && operationalProfileLoaded && !hasExplicitOperationalProfile
  );

  useEffect(() => {
    const deviceRevoked = readCookie(DEVICE_REVOKED_COOKIE);
    if (deviceRevoked !== '1') {
      return;
    }

    clearCookie(DEVICE_REVOKED_COOKIE);
    clearBrowserAuthArtifacts();
    clearDeviceFlowState({ preserveDeviceId: true });
    clearAuthState();

    if (pathname !== '/login') {
      router.replace('/login');
      router.refresh();
    }
  }, [pathname, router]);

  useEffect(() => {
    if (loading || isAuthPublicPath || shouldDeferLegalAcceptanceModal) {
      setLegalAcceptanceModalOpen(false);
      return;
    }

    setLegalAcceptanceModalOpen(userNeedsLegalAcceptance);
  }, [isAuthPublicPath, loading, shouldDeferLegalAcceptanceModal, userNeedsLegalAcceptance]);

  useEffect(() => {
    if (
      loading ||
      isAuthPublicPath ||
      shouldDeferLegalAcceptanceModal ||
      userNeedsLegalAcceptance ||
      !operationalProfileLoaded ||
      userNeedsOperationalProfileSelection
    ) {
      setIntroVideoModalOpen(false);
      return;
    }

    setIntroVideoModalOpen(userNeedsIntroVideoPrompt);
  }, [
    isAuthPublicPath,
    loading,
    operationalProfileLoaded,
    shouldDeferLegalAcceptanceModal,
    userNeedsLegalAcceptance,
    userNeedsIntroVideoPrompt,
    userNeedsOperationalProfileSelection,
  ]);

  useEffect(() => {
    if (
      loading ||
      isAuthPublicPath ||
      shouldDeferLegalAcceptanceModal ||
      userNeedsLegalAcceptance ||
      !operationalProfileLoaded
    ) {
      setOperationalProfileModalOpen(false);
      return;
    }

    setOperationalProfileModalOpen(userNeedsOperationalProfileSelection);
  }, [
    isAuthPublicPath,
    loading,
    operationalProfileLoaded,
    shouldDeferLegalAcceptanceModal,
    userNeedsLegalAcceptance,
    userNeedsOperationalProfileSelection,
  ]);

  const acceptLegalTerms = async () => {
    if (savingLegalAcceptance || !user) return;

    setSavingLegalAcceptance(true);
    const toastId = toast.loading(t('legalAcceptance.modalSaving'));

    try {
      const response = await fetch('/api/auth/legal-acceptance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.error || t('legalAcceptance.modalError'));
      }

      await fetchUserProfile(user);
      setLegalAcceptanceModalOpen(false);
      toast.success(t('legalAcceptance.modalSuccess'), { id: toastId });
    } catch (error: any) {
      toast.error(t('legalAcceptance.modalError'), { id: toastId, description: error?.message });
    } finally {
      setSavingLegalAcceptance(false);
    }
  };

  const dismissIntroVideoModal = async () => {
    if (!user || dismissingIntroVideo) return;

    setDismissingIntroVideo(true);
    const seenAt = new Date().toISOString();

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ platform_intro_video_seen_at: seenAt })
        .eq('id', user.id);

      if (error) throw error;

      setUserProfile((current: any) =>
        current ? { ...current, platform_intro_video_seen_at: seenAt } : current
      );
      setIntroVideoModalOpen(false);
    } catch (error: any) {
      toast.error(t('auth.toast.introVideoError'), {
        description: error?.message,
      });
    } finally {
      setDismissingIntroVideo(false);
    }
  };

  const setOperationalProfile = useCallback(
    async (nextProfile: GreenProOperationalProfile) => {
      if (!user?.id) {
        setOperationalProfileState(nextProfile);
        setHasExplicitOperationalProfile(true);
        return;
      }

      setOperationalProfileState(nextProfile);
      setHasExplicitOperationalProfile(true);
      setSavingOperationalProfile(true);

      try {
        const { error } = await supabase.from('user_preferences').upsert(
          {
            user_id: user.id,
            operational_profile: nextProfile,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        );

        if (error) throw error;
        setOperationalProfileModalOpen(false);
      } catch (error: any) {
        toast.error('Erro ao salvar perfil operacional.', { description: error?.message });
        await fetchOperationalProfile(user.id);
      } finally {
        setSavingOperationalProfile(false);
      }
    },
    [fetchOperationalProfile, supabase, user?.id]
  );

  useEffect(() => {
    setSubscriptionNowMs(Date.now());

    const expiresAt = userProfile?.subscription_expires_at;
    if (!expiresAt) return;

    const expiresAtMs = new Date(expiresAt).getTime();
    if (!Number.isFinite(expiresAtMs)) return;

    const now = Date.now();
    const remainingMs = expiresAtMs - now;
    const timeoutIds: number[] = [];
    let intervalId: number | null = null;

    const scheduleUpdateAt = (timestampMs: number) => {
      const delay = timestampMs - Date.now();
      if (delay <= 0) {
        setSubscriptionNowMs(Date.now());
        return;
      }

      const timeoutId = window.setTimeout(
        () => {
          setSubscriptionNowMs(Date.now());
        },
        Math.min(delay, 2_147_000_000)
      );

      timeoutIds.push(timeoutId);
    };

    scheduleUpdateAt(expiresAtMs + 1_000);

    if (remainingMs <= SUBSCRIPTION_EXPIRING_SOON_MS) {
      intervalId = window.setInterval(() => {
        setSubscriptionNowMs(Date.now());
      }, 60_000);
    } else {
      scheduleUpdateAt(expiresAtMs - SUBSCRIPTION_EXPIRING_SOON_MS + 1_000);
    }

    return () => {
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
    };
  }, [
    userProfile?.subscription_expires_at,
    userProfile?.subscription_plan,
    userProfile?.subscription_days_remaining,
  ]);

  // --- LOGICA DE BLOQUEIO POR ASSINATURA ---
  useEffect(() => {
    // Só executa se o carregamento terminou e existe um perfil
    if (!loading && userProfile) {
      const adminOnlyPaths = ['/admin-panel'];
      const shouldBlockAdminOnly = adminOnlyPaths.some(
        (path) => pathname === path || pathname.startsWith(`${path}/`)
      );
      const isOwner = userProfile.role === 'admin' || userProfile.is_super_admin === true;

      if (shouldBlockAdminOnly && !isOwner) {
        router.push('/dashboard');
        return;
      }

      if (subscriptionStatus.isFree) {
        const pathsRestritos = [
          // '/dashboard', // dashboard agora é liberado para todos
          '/betting-house-management',
          '/card-rules',
          '/sports-bets',
          '/surebets',
          '/promotions',
          '/casino-bets',
          '/bank-accounts',
          '/transactions',
          '/reports',
          '/partnerships',
          '/goals',
        ];

        const shouldRedirect = pathsRestritos.some(
          (path) => pathname === path || pathname.startsWith(`${path}/`)
        );

        if (shouldRedirect) {
          router.push('/plans');
        }
      }

      if (!subscriptionStatus.hasMonitorAccess) {
        const proOnlyPaths = ['/dashboard-opportunities'];
        const shouldRedirect = proOnlyPaths.some(
          (path) => pathname === path || pathname.startsWith(`${path}/`)
        );

        if (shouldRedirect) {
          router.push('/plans');
        }
      }
    }
  }, [
    userProfile,
    loading,
    pathname,
    router,
    subscriptionStatus.isFree,
    subscriptionStatus.hasMonitorAccess,
    subscriptionStatus.isOwner,
  ]); // 4. ADICIONE 'pathname' AQUI
  // ------------------------------------------

  useEffect(() => {
    // Em telas de autenticação, evita fetch de sessão mas deixa o effect re-executar
    // quando o usuário navegar para páginas protegidas após o login.
    if (isAuthPublicPath) {
      setLoading(false);
      return;
    }

    // Get initial session only on non-public/auth pages.
    setLoading(true);
    getSessionWithCache(true)
      .then(({ session, error }: { session: any; error: any }) => {
        if (error) {
          const msg = error.message || '';
          const code = error.code || '';
          const status = error.status;
          if (isRefreshTokenError(msg) || isLockError(msg)) {
            handleStaleSession();
            return;
          }
          if (isRateLimitError(msg, code, status)) {
            setLoading(false);
            return;
          }
        }
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) fetchUserProfile(session.user);
        setLoading(false);
      })
      .catch((err: any) => {
        const msg = err?.message || '';
        const code = err?.code || '';
        const status = err?.status;
        if (isRefreshTokenError(msg) || isLockError(msg)) {
          handleStaleSession();
        } else if (isRateLimitError(msg, code, status)) {
          setLoading(false);
        } else {
          clearAuthState();
        }
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: any, session: any) => {
      if (event === 'TOKEN_REFRESHED' && !session) {
        handleStaleSession();
        return;
      }
      if (event === 'SIGNED_OUT') {
        clearAuthState();
        return;
      }
      if (event === 'INITIAL_SESSION' && !session) {
        clearAuthState();
        return;
      }
      // If session exists but has a known-bad refresh token pattern, clear it
      if (session && !session.access_token) {
        handleStaleSession();
        return;
      }
      sessionCacheRef.current = { at: Date.now(), session: session ?? null };
      userCacheRef.current = { at: Date.now(), user: session?.user ?? null };
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session.user);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    // Suppress unhandled AbortError / Web Locks / Refresh Token errors from Supabase internals
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const msg = event?.reason?.message || '';
      const code = event?.reason?.code || '';
      const status = event?.reason?.status;
      if (
        isLockError(msg) ||
        isRefreshTokenError(msg) ||
        code === 'refresh_token_not_found' ||
        isRateLimitError(msg, code, status) ||
        event?.reason?.name === 'AbortError'
      ) {
        event.preventDefault();
        if (isRefreshTokenError(msg) || code === 'refresh_token_not_found') {
          handleStaleSession();
        }
      }
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [isAuthPublicPath]);

  // Realtime updates for user profile (plan, remaining days, role, etc.)
  useEffect(() => {
    if (!user?.id) return;

    return subscribeToUserRealtime(
      user.id,
      [{ table: 'user_profiles', filter: `id=eq.${user.id}` }],
      (change) => {
        if ((change as any)?.new) {
          setUserProfile((change as any).new);
        } else {
          fetchUserProfile(user);
        }
      }
    );
  }, [user?.id, supabase]);

  useEffect(() => {
    if (!user?.id) return;

    return subscribeToUserRealtime(
      user.id,
      [{ table: 'user_preferences', filter: `user_id=eq.${user.id}` }],
      () => {
        void fetchOperationalProfile(user.id);
      }
    );
  }, [fetchOperationalProfile, supabase, user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    let cancelled = false;
    let unsubscribe = () => {};

    const subscribeToCurrentDeviceRevocation = async () => {
      try {
        const currentDeviceId = ensureDeviceId();
        const currentDeviceHash = await hashDeviceTokenClient(currentDeviceId);
        if (cancelled) {
          return;
        }

        unsubscribe = subscribeToUserRealtime(
          user.id,
          [{ table: 'trusted_devices', filter: `user_id=eq.${user.id}` }],
          (change) => {
            const nextRecord = (change as any)?.new || (change as any)?.old;
            if (!nextRecord || nextRecord.device_token_hash !== currentDeviceHash) {
              return;
            }

            if (!(change as any)?.new?.revoked_at) {
              return;
            }

            clearCookie(DEVICE_REVOKED_COOKIE);
            clearBrowserAuthArtifacts();
            clearAuthState();
            router.replace('/login');
            router.refresh();
          }
        );
      } catch {
        // Keep auth usable even if device revocation subscription cannot be established.
      }
    };

    void subscribeToCurrentDeviceRevocation();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [router, supabase, user?.id]);

  // Email/Password Sign Up
  const signUp = useCallback(
    async (email: string, password: string, metadata = {}) => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: (metadata as any)?.fullName || '',
            avatar_url: (metadata as any)?.avatarUrl || '',
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
      return data;
    },
    [supabase]
  );

  // Email/Password Sign In
  const signIn = useCallback(
    async (email: string, password: string) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      return data;
    },
    [supabase]
  );

  // Sign Out
  const signOut = useCallback(async () => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;

    try {
      try {
        await fetch('/api/auth/access-session/close', {
          method: 'POST',
          credentials: 'include',
        });
      } catch {
        // Keep sign-out resilient even if access session close tracking fails.
      }

      const { error } = await supabase.auth.signOut();
      if (error) {
        const msg = error.message || '';
        if (!isRefreshTokenError(msg) && !isMissingSessionError(msg) && !isLockError(msg)) {
          throw error;
        }
      }
    } catch (err: any) {
      const msg = err?.message || '';
      if (!isRefreshTokenError(msg) && !isMissingSessionError(msg) && !isLockError(msg)) {
        throw err;
      }
    } finally {
      clearBrowserAuthArtifacts();
      clearDeviceFlowState({ preserveDeviceId: true });
      clearAuthState();
      signingOutRef.current = false;
      if (!isAuthPublicPath) {
        router.replace('/login');
        router.refresh();
      }
    }
  }, [clearAuthState, clearBrowserAuthArtifacts, isAuthPublicPath, router, supabase]);

  // Get Current User
  const getCurrentUser = useCallback(async () => {
    const { user, error } = await getUserWithCache();
    if (error) throw error;
    return user;
  }, []);

  useEffect(() => {
    if (!user?.id || !session?.access_token) {
      return;
    }

    let disposed = false;

    const sendHeartbeat = async (attempt = 0) => {
      if (disposed) {
        return;
      }

      try {
        const response = await fetch('/api/auth/access-session/heartbeat', {
          method: 'POST',
          credentials: 'include',
        });

        if (response.status === 409) {
          const body = await response.json().catch(() => ({}));
          const code = String(body?.code || '');
          if (code === 'single_session_enforced' || code === 'session_replaced') {
            // Avoid false-positive sign-outs during short synchronization windows
            // right after device approval/login on another browser.
            if (attempt === 0) {
              window.setTimeout(() => {
                void sendHeartbeat(1);
              }, 1200);
              return;
            }
            toast.error(
              body?.message ||
                'Por segurança, apenas um dispositivo pode permanecer conectado por conta. Detectamos um novo acesso e esta sessão foi encerrada automaticamente.'
            );
            await signOut();
            return;
          }
        }
      } catch {
        // Heartbeat failures should not impact the active session.
      }
    };

    const intervalId = window.setInterval(
      () => {
        if (document.visibilityState === 'visible') {
          void sendHeartbeat();
        }
      },
      5 * 60 * 1000
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void sendHeartbeat();
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      window.clearInterval(intervalId);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [session?.access_token, signOut, user?.id]);

  // Check if Email is Verified
  const isEmailVerified = useCallback(() => {
    return user?.email_confirmed_at !== null;
  }, [user?.email_confirmed_at]);

  // Get User Profile from Database
  const getUserProfile = useCallback(async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  }, [supabase, user]);

  const isAdmin = useCallback(() => {
    return userProfile?.role === 'admin' || userProfile?.is_super_admin === true;
  }, [userProfile?.is_super_admin, userProfile?.role]);

  const isSuperAdmin = useCallback(() => {
    return userProfile?.is_super_admin === true;
  }, [userProfile?.is_super_admin]);

  const refreshProfile = useCallback(() => {
    if (user) {
      return fetchUserProfile(user);
    }

    return Promise.resolve();
  }, [fetchUserProfile, user]);

  const value = useMemo(
    () => ({
      user,
      session,
      loading,
      userProfile,
      operationalProfile,
      operationalProfileLoaded,
      setOperationalProfile,
      subscriptionStatus,
      signUp,
      signIn,
      signOut,
      handleRevokedDeviceAccess,
      getCurrentUser,
      isEmailVerified,
      getUserProfile,
      isAdmin,
      isSuperAdmin,
      refreshProfile,
    }),
    [
      user,
      session,
      loading,
      userProfile,
      operationalProfile,
      operationalProfileLoaded,
      setOperationalProfile,
      subscriptionStatus,
      signUp,
      signIn,
      signOut,
      getCurrentUser,
      isEmailVerified,
      getUserProfile,
      isAdmin,
      isSuperAdmin,
      refreshProfile,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {legalAcceptanceModalOpen ? (
        <ModalPortal>
          <div className="modal-shell-overlay">
            <div className="modal-shell-panel max-w-2xl overflow-hidden">
              <div className="shrink-0 border-b border-white/10 px-5 py-4 sm:px-6">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-300/80">
                    {t('legalAcceptance.modalEyebrow')}
                  </p>
                  <p className="mt-2 text-lg font-black text-white sm:text-xl">
                    {t('legalAcceptance.modalTitle')}
                  </p>
                  <p className="mt-1 text-sm text-zinc-300">
                    {t('legalAcceptance.modalDescription')}
                  </p>
                </div>
              </div>
              <div className="space-y-4 px-5 py-5 sm:px-6">
                <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm leading-6 text-amber-50">
                  {t('legalAcceptance.modalBody')}
                </div>
                <div className="flex flex-wrap gap-3 text-sm">
                  <Link
                    href="/termos-de-servico"
                    target="_blank"
                    className="font-semibold text-brand-300 underline underline-offset-4"
                  >
                    {t('login.termsOfService')}
                  </Link>
                  <Link
                    href="/politica-de-privacidade"
                    target="_blank"
                    className="font-semibold text-brand-300 underline underline-offset-4"
                  >
                    {t('login.privacyPolicy')}
                  </Link>
                </div>
              </div>
              <div className="shrink-0 border-t border-white/10 bg-white/[0.02] px-5 py-4 sm:px-6">
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => signOut()}
                    className="inline-flex items-center justify-center rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-xs font-semibold text-red-300 transition-colors hover:bg-red-500/15"
                  >
                    {t('legalAcceptance.modalLogout')}
                  </button>
                  <button
                    type="button"
                    onClick={acceptLegalTerms}
                    disabled={savingLegalAcceptance}
                    className="inline-flex items-center justify-center rounded-lg bg-brand-700 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-60"
                  >
                    {savingLegalAcceptance
                      ? t('legalAcceptance.modalSaving')
                      : t('legalAcceptance.modalAccept')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      ) : null}
      <FirstLoginVideoModal
        open={introVideoModalOpen && !legalAcceptanceModalOpen}
        dismissing={dismissingIntroVideo}
        onDismiss={dismissIntroVideoModal}
      />
      {operationalProfileModalOpen ? (
        <ModalPortal>
          <div className="modal-shell-overlay items-end px-3 sm:items-center sm:px-4">
            <div className="modal-shell-panel mx-0 max-h-[calc(100vh-1rem)] w-full max-w-3xl overflow-hidden sm:w-full">
              <div className="shrink-0 border-b border-white/10 px-5 py-4 sm:px-6">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-300/80">
                  Perfil operacional
                </p>
                <p className="mt-2 text-lg font-black text-white sm:text-xl">
                  Escolha como a GreenPro deve se comportar
                </p>
                <p className="mt-1 text-sm text-zinc-300">
                  Esse modo controla sidebar, dashboards, menus, filtros e formularios sem reload
                  completo.
                </p>
              </div>
              <div className="min-h-0 space-y-4 overflow-y-auto px-5 py-5 sm:px-6">
                <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-3">
                  <p className="text-sm font-semibold text-amber-100">
                    Escolha uma opcao para comecar a usar a plataforma.
                  </p>
                  <p className="mt-1 text-xs leading-5 text-amber-50/90">
                    Sem definir um modo inicial, a GreenPro nao consegue liberar a experiencia
                    correta de menus, dashboards e formularios.
                  </p>
                </div>
                <OperationalProfileSwitcher
                  value={operationalProfile}
                  onChange={setOperationalProfile}
                  disabled={savingOperationalProfile}
                  allowCurrentSelection
                />
                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-zinc-300">
                  Voce podera trocar o perfil operacional quando quiser pelas configuracoes da sua
                  conta.
                </div>
              </div>
              <div className="shrink-0 border-t border-white/10 bg-white/[0.02] px-5 py-4 sm:px-6">
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => signOut()}
                    className="inline-flex items-center justify-center rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-xs font-semibold text-red-300 transition-colors hover:bg-red-500/15"
                  >
                    Sair da conta
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      ) : null}
    </AuthContext.Provider>
  );
};
